/**
 * Orders API Mutations
 *
 * Create/update operations.
 */

// API client
import { apiClient } from '../client';

// Types
import type { Order, CreateOrderPayload, UpdateOrderPayload } from './types';

export async function createOrder(payload: CreateOrderPayload): Promise<Order> {
  const response = await apiClient.post<{ order: Order }>('/api/orders', {
    order: payload,
  });
  return response.data.order;
}

export async function updateOrderStatus(
  id: number,
  status: 'lead' | 'confirmed' | 'cancelled',
  cancellationReason?: string
): Promise<Order> {
  let endpoint: string;
  const data: { cancellation_reason?: string } = {};

  switch (status) {
    case 'confirmed':
      endpoint = `/api/orders/${id}/confirm`;
      break;
    case 'cancelled':
      endpoint = `/api/orders/${id}/cancel`;
      if (cancellationReason) {
        data.cancellation_reason = cancellationReason;
      }
      break;
    case 'lead':
      endpoint = `/api/orders/${id}/revert_to_lead`;
      break;
  }

  const response = await apiClient.patch<{ order: Order }>(endpoint, data);
  return response.data.order;
}

export async function updateOrder(
  id: number,
  payload: UpdateOrderPayload
): Promise<Order> {
  const response = await apiClient.patch<{ order: Order }>(
    `/api/orders/${id}`,
    { order: payload }
  );
  return response.data.order;
}

// Re-persists the stored totals snapshot from the current live breakdowns —
// used by the drawer's Spine Refresh so "Order value" tracks Spine-side changes.
export async function recomputeOrderTotals(orderId: number): Promise<Order> {
  const response = await apiClient.post<{ order: Order }>(
    `/api/orders/${orderId}/recompute_totals`
  );
  return response.data.order;
}

// Sets every linked deal back to pending and queues the sync. The endpoint is
// deliberately status-blind -- the button is the only way in, and the UI
// already hides it where a retry cannot help.
export async function retryAttioSync(orderId: number): Promise<Order> {
  const response = await apiClient.post<{ order: Order }>(`/api/orders/${orderId}/attio_sync`);
  return response.data.order;
}

// ─── Generate Order Documents ────────────────────────────────────────────────

export type OrderDocumentType = 'quotation' | 'confirmation' | 'delivery_order';

export interface GenerateOrderDocumentsResponse {
  async_task: { id: number; item_count: number };
}

export async function generateOrderDocuments(
  orderId: number,
  documentTypes: OrderDocumentType[]
): Promise<GenerateOrderDocumentsResponse> {
  const response = await apiClient.post<GenerateOrderDocumentsResponse>(
    `/api/orders/${orderId}/generate_documents`,
    { document_types: documentTypes }
  );
  return response.data;
}

export interface GenerateOrderDocumentResponse {
  url: string;
}

export async function generateOrderDocument(
  orderId: number,
  documentType: OrderDocumentType
): Promise<GenerateOrderDocumentResponse> {
  const response = await apiClient.post<GenerateOrderDocumentResponse>(
    `/api/orders/${orderId}/documents/${documentType}`
  );
  return response.data;
}

export type XeroInvoiceMode = 'generate' | 'regenerate';

export interface CreateXeroInvoiceResponse {
  xero_invoice_id: string;
  xero_invoice_url: string;
  xero_contact_replaced: boolean;
}

export async function createXeroInvoice(
  orderId: number,
  mode: XeroInvoiceMode = 'generate'
): Promise<CreateXeroInvoiceResponse> {
  const response = await apiClient.post<CreateXeroInvoiceResponse>(
    `/api/orders/${orderId}/invoices`,
    { mode }
  );
  return response.data;
}
